import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";

import React from "react";

import type { TypoStyle } from "../Typo";
import type {
  ResponsiveTextButtonValue,
  TextButtonFocusStyle,
  TextButtonIconSize,
  TextButtonSize,
  TextButtonTone,
} from "./textButtonStyles";

import { Typo } from "../Typo";
import { textButtonStyles } from "./textButtonStyles";

const labelTypes = {
  xl: "labelXL",
  md: "labelMD",
  sm: "labelSM",
  xs: "labelXS",
  "2xs": "label2XS",
} as const satisfies Record<TextButtonSize, TypoStyle>;

type TextButtonBaseProps = {
  size?: ResponsiveTextButtonValue<TextButtonSize>;
  iconSize?: ResponsiveTextButtonValue<TextButtonIconSize>;
  tone?: TextButtonTone;
  focusStyle?: TextButtonFocusStyle;
  underlined?: boolean;
  iconL?: ReactNode;
  iconR?: ReactNode;
  disabled?: boolean;
};

export type TextButtonProps<T extends ElementType = "button"> =
  TextButtonBaseProps & { as?: T } & Omit<
      ComponentPropsWithoutRef<T>,
      keyof TextButtonBaseProps | "as"
    >;

export type {
  ResponsiveTextButtonValue,
  TextButtonFocusStyle,
  TextButtonIconSize,
  TextButtonSize,
  TextButtonTone,
};

export const TextButton = <T extends ElementType = "button">({
  as,
  size = "xl",
  iconSize,
  tone = "primary",
  focusStyle = "ring",
  disabled = false,
  underlined = false,
  className,
  iconL,
  iconR,
  children,
  onClick,
  ...otherProps
}: TextButtonProps<T>) => {
  const Element: ElementType = as || "button";
  const isButtonElement = Element === "button";
  const { href, ...restProps } = otherProps as typeof otherProps & {
    href?: string;
  };

  const handleClick = (event: React.MouseEvent) => {
    if (disabled && !isButtonElement) {
      event.preventDefault();
      return;
    }
    (onClick as ((event: React.MouseEvent) => void) | undefined)?.(event);
  };

  return (
    <Element
      className={textButtonStyles.container({
        size,
        iconSize,
        tone,
        focusStyle,
        underlined,
        isDisabled: disabled,
        className,
      })}
      onClick={handleClick}
      {...(isButtonElement
        ? {
            disabled,
            type:
              (otherProps as { type?: "button" | "submit" | "reset" }).type ??
              "button",
          }
        : {
            "aria-disabled": disabled || undefined,
            href: disabled ? undefined : href,
          })}
      {...restProps}
    >
      {iconL}
      {typeof size === "string" ? (
        children
      ) : (
        <Typo
          element="span"
          type={labelTypes[size.base]}
          smType={size.lg ? labelTypes[size.base] : undefined}
          lgType={size.lg ? labelTypes[size.lg] : undefined}
          textColor="text-inherit"
        >
          {children}
        </Typo>
      )}
      {iconR}
    </Element>
  );
};
